import { PLACEHOLDER_AVATAR_URL, PORTRAIT_PLACEHOLDER_PATH } from "../constants/images";
import {
  FilteringOperator,
  Person,
  PersonInfo,
  PersonsPage,
  SortDirection,
} from "../models/person.model";
import { BackendHttpService } from "./backendHttpService";
import { RequestOptions } from "./baseService";
import { GraphService } from "./graphService";

export interface PersonBasicInfo {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface PersonsByIdsRequest {
  personIds: number[];
  inactive?: boolean;
  sortField?: string;
  sortDirection?: string;
  officeLocation?: string;
}

export interface PersonFilterModel {
  pageIndex: number;
  pageSize: number;
  filterKeywords?: string[];
  inactive: boolean;
  availableInDays?: number | null;
  sortField?: string;
  sortDirection: SortDirection;
  officeLocationId?: number;
  matchTechThreshold: number;
  matchCertThreshold: number;
  maxTechMatches: number;
  maxCertMatches: number;
  exactMatchOnly: boolean;
  exactTechMatchOnly: boolean;
  exactCertMatchOnly: boolean;
  useTechSearch: boolean;
  useCertSearch: boolean;
  filteringOperator: FilteringOperator;
  languageId?: number;
  proficiencyId?: number;
}

export interface GetPersonsPageRequest {
  filterModel: PersonFilterModel;
}

export class PersonService extends BackendHttpService {
  private currentPersonFirstName: string = "";
  private currentPersonLastName: string = "";
  private currentPersonImg: string = "";

  constructor() {
    super("person");
  }

  setCurrentUserName(firstName: string, lastName: string): void {
    this.currentPersonFirstName = firstName;
    this.currentPersonLastName = lastName;
  }

  setCurrentUserImg(imgPath: string): void {
    this.currentPersonImg =
      imgPath === undefined
        ? PORTRAIT_PLACEHOLDER_PATH
        : imgPath;
  }

  protected async putFormDataSafe<T extends FormData>(
    formData: T,
    path: string
  ): Promise<Response> {
    const accessToken = await this.getAccessToken();

    const currentLanguage = localStorage.getItem("language") || "en";
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      "Accept-Language": currentLanguage,
    };

    const response = await fetch(this.buildUrl(path), {
      method: "PUT",
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response;
  }

  async getPersonByID(id: number): Promise<Person> {
    try {
      return await this.get<Person>(`/${id}`);
    } catch (error) {
      console.error(`Error getting person with ID ${id}:`, error);
      throw error;
    }
  }

  async updatePhoto(
    personId: number,
    personData: {
      firstName: string;
      lastName: string;
      email: string;
      image: string;
    }
  ): Promise<void> {
    try {
      if (!personId || isNaN(personId)) {
        throw new Error("Invalid person ID for photo update");
      }

      if (!personData.firstName || !personData.lastName || !personData.email) {
        throw new Error("Missing required fields for photo update");
      }

      const formData = new FormData();

      formData.append("Firstname", personData.firstName.trim());
      formData.append("Lastname", personData.lastName.trim());
      formData.append("Email", personData.email.trim());
      formData.append("Id", personId.toString());

      if (personData.image) {
        let imageToProcess = personData.image;

        if (!imageToProcess.startsWith("data:")) {
          if (imageToProcess.match(/^[A-Za-z0-9+/=]+$/)) {
            imageToProcess = `data:image/jpeg;base64,${imageToProcess}`;
            console.log("Added data:image prefix to base64 string");
          }
        }

        if (imageToProcess.startsWith("data:")) {
          const { blob, fileName } = this.convertBase64ToBlob(imageToProcess);

          console.log("Uploading photo:", {
            personId,
            blobSize: blob.size,
            fileName,
            blobType: blob.type,
          });

          formData.append("Image", blob, fileName);
          formData.append("ImagePath", fileName);
        } else {
          console.warn("Image format not recognized, skipping upload");
        }
      }

      await this.putFormData<FormData, void>(formData, `/${personId}`);
    } catch (error) {
      console.error(`Error updating person photo for ID ${personId}:`, error);
      throw error;
    }
  }

  private convertBase64ToBlob(base64Image: string): {
    blob: Blob;
    fileName: string;
  } {
    const matches = base64Image.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);

    if (!matches || matches.length !== 3) {
      throw new Error("Invalid base64 image format");
    }

    const type = matches[1];
    const base64Data = matches[2];
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type });
    const fileName = `photo_${Date.now()}.${type.split("/")[1]}`;

    return { blob, fileName };
  }

  private appendImagePath(user: Person, formData: FormData): void {
    const imagePath =
      typeof user.image === "string"
        ? user.image
        : user.image?.name ?? user.imagePath;
    if (imagePath) {
      formData.append("imagePath", imagePath);
    }
  }

  async getPersonsByPageAndFilter(
    pageSize: number,
    pageIndex: number,
    sortField: string,
    sortDirection: SortDirection,
    filterKeywords: string[] = [],
    inactive: boolean = false,
    availableInDays: number | null = null,
    officeLocation?: string,
    matchTechThreshold: number = 0.85,
    matchCertThreshold: number = 0.85,
    maxTechMatches: number = 5,
    maxCertMatches: number = 5,
    exactMatchOnly: boolean = false,
    exactTechMatchOnly: boolean = false,
    exactCertMatchOnly: boolean = false,
    useTechSearch: boolean = true,
    useCertSearch: boolean = true,
    filteringOperator: FilteringOperator = FilteringOperator.Or,
    languageId?: number,
    proficiencyId?: number,
    options: RequestOptions = {}
  ): Promise<PersonsPage> {
    try {
      // Updated request structure to match simplified backend API
      // Backend now expects GetPersonsPageRequest with nested FilterModel
      const request: GetPersonsPageRequest = {
        filterModel: {
          pageIndex,
          pageSize,
          filterKeywords,
          inactive,
          availableInDays,
          sortField,
          sortDirection,
          officeLocationId: officeLocation ? parseInt(officeLocation) : undefined,
          matchTechThreshold,
          matchCertThreshold,
          maxTechMatches,
          maxCertMatches,
          exactMatchOnly,
          exactTechMatchOnly,
          exactCertMatchOnly,
          useTechSearch,
          useCertSearch,
          filteringOperator,
          languageId,
          proficiencyId,
        },
      };

      const extendedTimeout = 15 * 60 * 1000; // 15 minutes

      return await this.post<GetPersonsPageRequest, PersonsPage>(request, "/get-page", {
        timeout: extendedTimeout,
        ...options,
      });
    } catch (error) {
      if ((error as any).name === "AbortError") {
        throw error;
      }
      console.error("Error fetching persons:", error);
      throw error;
    }
  }

  async getPersonsPage(
    request: GetPersonsPageRequest,
    options: RequestOptions = {}
  ): Promise<PersonsPage> {
    try {
      const extendedTimeout = 15 * 60 * 1000;
      return await this.post<GetPersonsPageRequest, PersonsPage>(request, "/get-page", {
        timeout: extendedTimeout,
        ...options,
      });
    } catch (error) {
      if ((error as any).name === "AbortError") {
        throw error;
      }
      console.error("Error fetching persons page:", error);
      throw error;
    }
  }

  async getPersonById(id: number): Promise<Person> {
    return this.getPersonByID(id);
  }

  async createPerson(person: Person): Promise<Person> {
    try {
      const formData = new FormData();
      formData.append("Firstname", person.firstName);
      formData.append("Lastname", person.lastName);
      formData.append("Email", person.email);

      if (person.officeLocationName) {
        formData.append("OfficeLocation", person.officeLocationName);
      }

      if (
        typeof person.image === "string" &&
        person.image.startsWith("data:")
      ) {
        const { blob, fileName } = this.convertBase64ToBlob(person.image);
        formData.append("Image", blob, fileName);
        formData.append("ImagePath", fileName);
      } else if (typeof person.image === "string") {
        formData.append("ImagePath", person.image);
      } else if (person.image) {
        formData.append("Image", person.image as File);
        this.appendImagePath(person, formData);
      }

      return await this.postFormData<FormData, Person>(formData);
    } catch (error: any) {
      if (
        error.status === 400 &&
        error.error?.error === "CV with email already exists!"
      ) {
        throw new Error(
          "CV with provided email already exists. Please edit it instead."
        );
      }
      console.error("Error creating person:", error);
      throw error;
    }
  }

  async updatePerson(
    personId: number,
    personData: Partial<Person>
  ): Promise<void> {
    try {
      const formData = new FormData();

      formData.append("Id", personId.toString());
      formData.append("Firstname", personData.firstName || "");
      formData.append("Lastname", personData.lastName || "");
      formData.append("Email", personData.email || "");

      if (personData.officeLocationId !== undefined) {
        formData.append(
          "OfficeLocationId",
          personData.officeLocationId.toString()
        );
      } else if (personData.officeLocationName) {
        formData.append("OfficeLocation", personData.officeLocationName);
      }

      if (
        personData.image &&
        typeof personData.image === "string" &&
        personData.image.startsWith("data:")
      ) {
        const { blob, fileName } = this.convertBase64ToBlob(personData.image);
        formData.append("Image", blob, fileName);
        formData.append("ImagePath", fileName);
      } else if (personData.image) {
        formData.append("Image", personData.image);
      }

      if (
        personData.imagePath &&
        !(
          typeof personData.image === "string" &&
          personData.image.startsWith("data:")
        )
      ) {
        formData.append("ImagePath", personData.imagePath);
      }

      if (personData.inactive !== undefined) {
        formData.append("Inactive", personData.inactive.toString());
      }

      if (personData.availabilityDate) {
        formData.append("AvailabilityDate", personData.availabilityDate);
      }

      await this.putFormData<FormData, void>(formData, `/${personId}`);
    } catch (error) {
      console.error(`Error updating person with ID ${personId}:`, error);
      throw error;
    }
  }

  async changePersonCvActivityStatus(person: Person): Promise<Person> {
    try {
      const formData = new FormData();

      const newInactiveStatus = !person.inactive;
      formData.append("inactive", `${newInactiveStatus}`);

      const response = await this.putFormDataSafe(
        formData,
        `/${person.id}/UpdateInactivity`
      );

      const responseText = await response.text();
      if (!responseText || responseText.trim() === "") {
        const updatedPerson = { ...person };
        updatedPerson.inactive = newInactiveStatus;
        return updatedPerson;
      }

      try {
        // Try to parse as JSON if there's content
        const jsonResponse = JSON.parse(responseText);
        return jsonResponse;
      } catch (parseError) {
        const updatedPerson = { ...person };
        updatedPerson.inactive = newInactiveStatus;
        return updatedPerson;
      }
    } catch (error) {
      console.error(
        `Error changing activity status for person with id ${person.id}:`,
        error
      );
      throw error;
    }
  }

  async changeAssignment(personId: number, availabilityDate: string | undefined): Promise<void> {
    try {
      const formData = new FormData();
      if (availabilityDate) {
        formData.append("availabilityDate", availabilityDate);
      }

      await this.putFormDataSafe(
        formData,
        `/${personId}/UpdateAssignment`
      );
    } catch (error) {
      console.error(
        `Error changing assignment for person with id ${personId}:`,
        error
      );
      throw error;
    }
  }

  async deletePerson(person: Person): Promise<void> {
    try {
      await this.deleteMultiplePersons([person.id]);
    } catch (error) {
      console.error(`Error deleting person with id ${person.id}:`, error);
      throw error;
    }
  }

  async deleteMultiplePersons(personIds: number[]): Promise<void> {
    try {
      await this.deleteWithBody({ usersIds: personIds }, "/batch-delete");
    } catch (error) {
      console.error("Error deleting multiple persons:", error);
      throw error;
    }
  }

  private async deleteWithBody(data: any, path: string = ""): Promise<void> {
    const accessToken = await this.getAccessToken();

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      "Accept-Language": localStorage.getItem("language") || "en",
    };

    const response = await fetch(this.buildUrl(path), {
      method: "DELETE",
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
  }

  async removePhoto(personId: number): Promise<void> {
    try {
      const person = await this.getPersonById(personId);

      const formData = new FormData();
      formData.append("Id", personId.toString());
      formData.append("Firstname", person.firstName);
      formData.append("Lastname", person.lastName);
      formData.append("Email", person.email);

      formData.append("ImagePath", "");

      await this.putFormData<FormData, void>(formData, `/${personId}`);
    } catch (error) {
      console.error(`Error removing photo for person ID ${personId}:`, error);
      throw error;
    }
  }

  async fetchMicrosoftAccountPhoto(personId: number): Promise<string> {
    try {
      const graphService = new GraphService();

      const photoBase64 = await graphService.getCurrentUserPhotoAsBase64();

      if (photoBase64) {
        return photoBase64;
      }

      return PLACEHOLDER_AVATAR_URL;
    } catch (error) {
      console.error(
        `Error fetching Microsoft account photo for person ID ${personId}:`,
        error
      );
      return PLACEHOLDER_AVATAR_URL;
    }
  }

  async getBasicInfo(options: RequestOptions = {}): Promise<PersonBasicInfo[]> {
    try {
      return await this.get<PersonBasicInfo[]>("/basic-info", options);
    } catch (error) {
      console.error("Error fetching person basic info:", error);
      throw error;
    }
  }

  async getPersonsByIds(
    request: PersonsByIdsRequest,
    options: RequestOptions = {}
  ): Promise<PersonInfo[]> {
    try {
      return await this.post<PersonsByIdsRequest, PersonInfo[]>(
        request,
        "/get-by-ids",
        options
      );
    } catch (error) {
      console.error("Error fetching persons by IDs:", error);
      throw error;
    }
  }

  async syncOfficeLocationFromGraph(
    personId: number,
    options: RequestOptions = {}
  ): Promise<Person> {
    try {
      return await this.post<void, Person>(
        undefined,
        `/${personId}/sync-office-location`,
        options
      );
    } catch (error) {
      console.error(
        `Error syncing office location for person ID ${personId}:`,
        error
      );
      throw error;
    }
  }
}
